/**
 * Quick navigation (#quicknav, see site/_quicknav.scss).
 *
 * Everything here depends on two heights, neither of them constant:
 *
 * - #wrapper-header sticks to the top and differs in height per breakpoint,
 *   depending on whether the navigation band is rendered.
 * - The bar itself wraps on narrow displays and grows taller by doing so.
 *
 * Both are measured and written to <html> as --sticky-head-h and --quicknav-h.
 * The SCSS uses them for the "top" of the bar and for the scroll-margin-top of
 * the sections - without it the headings end up behind head and bar on an
 * anchor jump.
 *
 * The active marker is computed here instead of by Bootstrap's ScrollSpy
 * (PMB-203): ScrollSpy only marks a section once its top edge enters a narrow
 * band below the head. The last sections of the advisor data sheet are short and
 * share the final screen - the page cannot be scrolled far enough for their top
 * edges to ever reach that band, so they stayed unmarked for good, even after
 * being clicked.
 *
 * On the data sheet only sections 1 to 3 can push their top edge to the reading
 * line; 4 to 7 would need more scroll than the page has. A fixed line therefore
 * stops at section 3 and jumps to the last section at the very bottom, skipping
 * everything in between - which is why the line travels down to the bottom of
 * the viewport over the final screen, see _line().
 */
export default class QuickNavController {
  private static readonly HEAD_PROPERTY = '--sticky-head-h';
  private static readonly NAV_PROPERTY = '--quicknav-h';
  private static readonly SMOOTH_CLASS = 'js-smooth-anchors';
  private static readonly ACTIVE_CLASS = 'active';

  /** Counts as "page cannot scroll" - zoom levels yield fractions of pixels. */
  private static readonly BOTTOM_TOLERANCE = 2;

  /**
   * A click marks its section and keeps the marker until the page moves away
   * from the position it jumped to. Without that threshold the slight wobble at
   * the jump target would already release it.
   */
  private static readonly RELEASE_DISTANCE = 8;

  /** Quiet time after the last scroll event, then the jump counts as arrived. */
  private static readonly SETTLE_DELAY = 150;

  private readonly header: HTMLElement | null;
  private readonly nav: HTMLElement | null;
  private readonly links: HTMLAnchorElement[] = [];
  private readonly sections: HTMLElement[] = [];

  private offset = 0;
  private activeIndex = -1;

  /** Clicked section plus the position where its jump came to a stop. */
  private pinnedIndex: number | null = null;
  private pinnedScrollY: number | null = null;

  private frame = 0;
  private settleTimer = 0;

  constructor() {
    this.header = document.querySelector('#wrapper-header');
    this.nav = document.querySelector('#quicknav');

    if (this.header === null && this.nav === null) {
      return;
    }

    if (this.nav !== null) {
      // Only where a quick navigation exists: smooth out anchor jumps. As a
      // class rather than a style here so prefers-reduced-motion applies in SCSS.
      document.documentElement.classList.add(QuickNavController.SMOOTH_CLASS);
      this._collect();
      this._listen();
      this._serveSectionFields();
      this._pinFromHash();
    }

    this._measure();
    this._observe();
  }

  /**
   * Only entries whose target exists - that keeps bar and sections paired up
   * even when a section is dropped from the template.
   */
  private _collect(): void {
    this.nav?.querySelectorAll<HTMLAnchorElement>('a.nav-link[href^="#"]').forEach((link) => {
      const section = document.querySelector<HTMLElement>(link.getAttribute('href') ?? '');

      if (section === null) {
        return;
      }

      this.links.push(link);
      this.sections.push(section);
    });
  }

  private _listen(): void {
    this.links.forEach((link, index) => {
      link.addEventListener('click', () => {
        this.pinnedIndex = index;
        this.pinnedScrollY = null;
        this._mark(index);
        // Start this even when the click does not scroll at all - say on the
        // last section with the page already at its end. Otherwise the marker
        // would get no reference position and could never be released.
        this._scheduleSettle();
      });
    });

    window.addEventListener('scroll', () => this._onScroll(), { passive: true });
  }

  /**
   * Arriving with a section in the URL (the server sends the page back to the
   * section after "Speichern") counts like clicking its entry: the browser
   * lands the heading below the reading line by the scroll margin, so the
   * plain measurement would mark the section before it.
   */
  private _pinFromHash(): void {
    const index = this.sections.findIndex((section) => `#${section.id}` === window.location.hash);

    if (index === -1) {
      return;
    }

    this.pinnedIndex = index;
    this.pinnedScrollY = null;
    this._mark(index);
    this._scheduleSettle();
  }

  /**
   * A form can ask which section is active: a hidden input carrying
   * data-quicknav-section receives the id of the marked section when its form
   * is submitted. The server can then send the page back to that anchor - the
   * "Speichern" of the advisor data sheet stays in the section this way.
   */
  private _serveSectionFields(): void {
    document.querySelectorAll<HTMLInputElement>('input[data-quicknav-section]').forEach((field) => {
      field.form?.addEventListener('submit', () => {
        field.value = this.activeIndex >= 0 ? this.sections[this.activeIndex].id : '';
      });
    });
  }

  /**
   * Write the heights and follow up on the marker when the offset changed.
   */
  private _measure(): void {
    const headHeight = this._height(this.header);
    const navHeight = this._height(this.nav);
    const root = document.documentElement;

    if (this.header !== null) {
      root.style.setProperty(QuickNavController.HEAD_PROPERTY, `${headHeight}px`);
    }

    if (this.nav === null) {
      return;
    }

    root.style.setProperty(QuickNavController.NAV_PROPERTY, `${navHeight}px`);

    this.offset = headHeight + navHeight;
    this._update();
  }

  private _height(element: HTMLElement | null): number {
    return element === null ? 0 : Math.round(element.getBoundingClientRect().height);
  }

  private _onScroll(): void {
    this._scheduleSettle();

    if (this.frame !== 0) {
      return;
    }

    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      this._update();
    });
  }

  /**
   * The jump counts as arrived once the page has been quiet for a moment - every
   * scroll event pushes that moment back.
   */
  private _scheduleSettle(): void {
    window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => this._settle(), QuickNavController.SETTLE_DELAY);
  }

  /** End of the jump: from here every further move counts as the user scrolling. */
  private _settle(): void {
    if (this.pinnedIndex === null || this.pinnedScrollY !== null) {
      return;
    }

    this.pinnedScrollY = window.scrollY;
  }

  /**
   * Active is the last section whose top edge has passed the reading line.
   */
  private _update(): void {
    if (this.sections.length === 0) {
      return;
    }

    if (this._keepsPinned()) {
      this._mark(this.pinnedIndex as number);
      return;
    }

    const line = this._line();
    let index = 0;

    this.sections.forEach((section, position) => {
      if (section.getBoundingClientRect().top <= line + 1) {
        index = position;
      }
    });

    this._mark(index);
  }

  /**
   * The reading line sits below head and bar, and travels down to the bottom of
   * the viewport over the last screen of the page.
   *
   * Sections that share the final screen cannot reach a fixed line - the page
   * runs out of scroll first - and would never be marked. Moving the line over
   * exactly that stretch gives each of them its own range: while more than one
   * screen is left the line does not move at all, so scrolling behaves as before
   * everywhere else.
   */
  private _line(): number {
    const view = window.innerHeight;
    const span = view - this.offset;
    const scrollable = document.documentElement.scrollHeight - view;

    // A page that fits on screen has no final stretch to spread out, and without
    // room below the bar there is nothing to travel through either.
    if (scrollable <= QuickNavController.BOTTOM_TOLERANCE || span <= 0) {
      return this.offset;
    }

    const remaining = scrollable - window.scrollY;
    const travelled = Math.min(1, Math.max(0, 1 - remaining / span));

    return this.offset + span * travelled;
  }

  private _keepsPinned(): boolean {
    if (this.pinnedIndex === null) {
      return false;
    }

    // Jump still running - its end position is only known once _settle() ran.
    if (this.pinnedScrollY === null) {
      return true;
    }

    if (Math.abs(window.scrollY - this.pinnedScrollY) <= QuickNavController.RELEASE_DISTANCE) {
      return true;
    }

    this.pinnedIndex = null;
    this.pinnedScrollY = null;

    return false;
  }

  private _mark(index: number): void {
    if (index === this.activeIndex) {
      return;
    }

    this.activeIndex = index;

    this.links.forEach((link, position) => {
      const active = position === index;

      link.classList.toggle(QuickNavController.ACTIVE_CLASS, active);

      if (active) {
        link.setAttribute('aria-current', 'true');
        return;
      }

      link.removeAttribute('aria-current');
    });
  }

  private _observe(): void {
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this._measure());

      if (this.header !== null) {
        observer.observe(this.header);
      }

      if (this.nav !== null) {
        observer.observe(this.nav);
      }

      return;
    }

    window.addEventListener('resize', () => this._measure());
  }
}
